export type PublicKeyCredentialCreationOptionsJSON = PublicKeyCredentialCreationOptions;
export type PublicKeyCredentialRequestOptionsJSON = PublicKeyCredentialRequestOptions;

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const pad = base64url.length % 4 === 0 ? '' : '='.repeat(4 - (base64url.length % 4));
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const str = atob(base64);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i);
  return bytes.buffer;
}

export function useWebAuthn() {
  async function createCredential(options: PublicKeyCredentialCreationOptionsJSON) {
    if (!('credentials' in navigator) || !window.PublicKeyCredential) {
      throw new Error('WebAuthn not supported in this browser');
    }
    const credential = await navigator.credentials.create({ publicKey: options });
    if (!credential) throw new Error('Credential creation failed');
    const resp = credential as PublicKeyCredential & { response: AuthenticatorAttestationResponse & { getPublicKey?: () => ArrayBuffer } };
    let spkiBase64: string | null = null;
    try {
      if (typeof resp.response.getPublicKey === 'function') {
        const spki = resp.response.getPublicKey();
        if (spki) spkiBase64 = bufferToBase64url(spki);
      }
    } catch {}
    return {
      id: resp.id,
      rawId: bufferToBase64url(resp.rawId),
      type: resp.type,
      response: {
        clientDataJSON: bufferToBase64url(resp.response.clientDataJSON),
        attestationObject: bufferToBase64url(resp.response.attestationObject),
      },
      publicKeySpki: spkiBase64,
    };
  }

  async function getAssertion(options: PublicKeyCredentialRequestOptionsJSON) {
    if (!('credentials' in navigator) || !window.PublicKeyCredential) {
      throw new Error('WebAuthn not supported in this browser');
    }
    const assertion = await navigator.credentials.get({ publicKey: options });
    if (!assertion) throw new Error('Assertion failed');
    const cred = assertion as PublicKeyCredential & { response: AuthenticatorAssertionResponse };
    return {
      id: cred.id,
      rawId: bufferToBase64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufferToBase64url(cred.response.clientDataJSON),
        authenticatorData: bufferToBase64url(cred.response.authenticatorData),
        signature: bufferToBase64url(cred.response.signature),
        userHandle: cred.response.userHandle ? bufferToBase64url(cred.response.userHandle) : null,
      },
    };
  }

  function buildCreationOptions({ challenge, rpId, userId, userName }: { challenge: string; rpId: string; userId: string; userName: string; }): PublicKeyCredentialCreationOptionsJSON {
    return {
      rp: { name: 'DeviceBound Wallet', id: rpId },
      user: {
        id: new TextEncoder().encode(userId),
        name: userName,
        displayName: userName,
      },
      challenge: base64urlToBuffer(challenge),
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
      ],
      timeout: 60_000,
      authenticatorSelection: {
        userVerification: 'required',
        residentKey: 'required',
        requireResidentKey: true,
      },
      attestation: 'none',
    };
  }

  function buildRequestOptions({ challenge, allowCredentials }: { challenge: string; allowCredentials?: Array<{ id: string; type: PublicKeyCredentialType }> }): PublicKeyCredentialRequestOptionsJSON {
    return {
      challenge: base64urlToBuffer(challenge),
      timeout: 60_000,
      userVerification: 'required',
      allowCredentials: allowCredentials?.map(c => ({ id: base64urlToBuffer(c.id) as any, type: c.type })) as any,
    };
  }

  return { createCredential, getAssertion, buildCreationOptions, buildRequestOptions };
}


